import React, { useEffect, useRef } from 'react';
import { BRAND_COLORS } from './constants';

// Version 1: Pixelated Code Background (Enhanced with Animation and Configuration)
export const PixelatedCodeBackground: React.FC<{ defaultOpacity?: number }> = ({ defaultOpacity = 80 }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Configuration state
        let config = {
            fontSize: 12,
            density: 30,
            speed: 2,
            randomness: 3,
            fadeStrength: 15,
            globalOpacity: defaultOpacity,
            globalScale: 100
        };

        // Create pixelated code pattern with animation
        const codeChars = ['0', '1', '<', '>', '/', '{', '}', '(', ')', ';', '=', '+', '-', 'x', 'y', 'z', 'a', 'b', 'c'];

        // Store character data for animation
        interface CharData {
            char: string;
            x: number;
            y: number;
            opacity: number;
            nextChangeTime: number;
        }

        let chars: CharData[] = [];

        // Initialize characters with configurable density
        const initializeCharacters = () => {
            chars = [];
            const pixelSize = config.fontSize * (config.globalScale / 100);

            for (let x = 0; x < canvas.width; x += pixelSize * 2) {
                for (let y = pixelSize * 2; y < canvas.height; y += pixelSize * 2) {
                    if (Math.random() < (config.density / 100)) {
                        chars.push({
                            char: codeChars[Math.floor(Math.random() * codeChars.length)],
                            x,
                            y,
                            opacity: (config.fadeStrength / 100) + Math.random() * (config.fadeStrength / 100),
                            nextChangeTime: Date.now() + Math.random() * (config.randomness * 1000) + 500
                        });
                    }
                }
            }
        };

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initializeCharacters();
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // Listen for configuration changes
        const handleConfigChange = (event: CustomEvent) => {
            const newConfig = event.detail;
            config = {
                fontSize: newConfig.v1FontSize || config.fontSize,
                density: newConfig.v1Density || config.density,
                speed: newConfig.v1Speed || config.speed,
                randomness: newConfig.v1Randomness || config.randomness,
                fadeStrength: newConfig.v1FadeStrength || config.fadeStrength,
                globalOpacity: newConfig.globalOpacity || config.globalOpacity,
                globalScale: newConfig.globalScale || config.globalScale
            };
            initializeCharacters(); // Reinitialize with new density
        };

        window.addEventListener('backgroundConfigChange', handleConfigChange as EventListener);

        initializeCharacters();

        let animationFrameId: number;

        const animate = () => {
            ctx.fillStyle = BRAND_COLORS.background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const now = Date.now();
            const pixelSize = config.fontSize * (config.globalScale / 100);

            // Draw and update characters
            chars.forEach(charData => {
                // Randomly change character based on speed
                if (now > charData.nextChangeTime) {
                    charData.char = codeChars[Math.floor(Math.random() * codeChars.length)];
                    charData.nextChangeTime = now + Math.random() * (config.randomness * 1000) + (500 / config.speed);
                    charData.opacity = (config.fadeStrength / 100) + Math.random() * (config.fadeStrength / 100);
                }

                const globalOpacityMultiplier = config.globalOpacity / 100;
                const finalOpacity = charData.opacity * globalOpacityMultiplier;

                ctx.fillStyle = `${BRAND_COLORS.primary}${Math.floor(finalOpacity * 255).toString(16).padStart(2, '0')}`;
                ctx.font = `${pixelSize * 2}px monospace`;
                ctx.fillText(charData.char, charData.x, charData.y);
            });

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('backgroundConfigChange', handleConfigChange as EventListener);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none z-0"
            style={{ background: BRAND_COLORS.background }}
        />
    );
};
